import React, { useState, useEffect } from "react";
import FileTypeModal from "./FileTypeModal";
import { getFileExtension, getFileName, resolveFileUrl } from "./fileUtils";

const XERFileViewer = ({ file, open, onClose, projectId }) => {
  const fileName = getFileName(file);
  const extension = getFileExtension(file);
  const fileUrl = resolveFileUrl(file, projectId);
  const [parsedContent, setParsedContent] = useState([]);
  const [rawContent, setRawContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'raw'

  useEffect(() => {
    if (open && fileUrl) {
      setLoading(true);
      setError(null);
      fetch(fileUrl)
        .then((response) => response.text())
        .then((content) => {
          setRawContent(content);
          const lines = content.split('\n');
          // Filter out empty lines and process
          const processedLines = lines
            .filter(line => line.trim() !== '')
            .map(line => {
              // Handle tab-separated values
              const columns = line.split('\t');
              return columns;
            });
          setParsedContent(processedLines);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error loading XER file:', err);
          setError('Failed to load file');
          setLoading(false);
        });
    }
  }, [open, fileUrl]);

  const handleDownload = () => {
    if (parsedContent.length === 0) return;
    
    const xerContent = parsedContent
      .map(row => row.join('\t'))
      .join('\n');
    
    const blob = new Blob([xerContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'data.xer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <FileTypeModal open={open} onClose={onClose} title={fileName || "XER File"}>
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center p-4">
            <div className="text-gray-500">Loading file...</div>
          </div>
        ) : error ? (
          <div className="space-y-3">
            <p className="text-sm text-red-500">{error}</p>
            <div className="flex gap-3">
              <a
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Open File
              </a>
              <a
                href={fileUrl}
                download={fileName}
                className="rounded bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
              >
                Download
              </a>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 text-sm rounded ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Table View
                </button>
                <button
                  onClick={() => setViewMode('raw')}
                  className={`px-3 py-1 text-sm rounded ${viewMode === 'raw' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Raw Text
                </button>
              </div>
              <button
                onClick={handleDownload}
                className="rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
              >
                Download
              </button>
            </div>
            
            {viewMode === 'table' ? (
              <div className="max-h-96 overflow-auto border rounded bg-white">
                <table className="min-w-full border-collapse text-xs">
                  <tbody>
                    {parsedContent.slice(0, 100).map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="border px-2 py-1 bg-gray-100 text-gray-600 w-10">{rowIndex + 1}</td>
                        {row.map((cell, cellIndex) => (
                          <td 
                            key={cellIndex}
                            className="border px-2 py-1 whitespace-nowrap text-gray-800"
                            style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {cell || ' '}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedContent.length > 100 && (
                  <p className="p-2 text-sm text-gray-500 text-center">
                    Showing first 100 rows of {parsedContent.length} total rows
                  </p>
                )}
              </div>
            ) : (
              <pre className="max-h-96 overflow-auto border rounded p-4 text-xs bg-white text-gray-800 whitespace-pre-wrap">
                {rawContent}
              </pre>
            )}
          </>
        )}
      </div>
    </FileTypeModal>
  );
};

export default XERFileViewer;
